import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * SEO Component for dynamic meta tags per page
 * Use this component on individual pages to override default meta tags
 */
const SEO = ({ 
  title, 
  description, 
  keywords, 
  ogTitle, 
  ogDescription, 
  ogImage, 
  ogUrl,
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  twitterImage,
  canonicalUrl,
  noindex = false
}) => {
  const defaultTitle = "GeneFlou Genetic Counselling - Helping Families Flourish";
  const defaultDescription = "Compassionate genetic counselling services to help families understand hereditary conditions, make informed health decisions, and plan for a healthy future.";
  const defaultKeywords = "genetic counselling, genetic testing, hereditary conditions, family health planning, genetic counselor, prenatal genetic counselling, cancer genetics";
  const defaultOgImage = "/og-image.png";
  const defaultTwitterImage = "/og-image.png";
  const siteUrl = process.env.REACT_APP_SITE_URL || "https://geneflou.com";

  // Warn in development if environment variables are not set
  if (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_SITE_URL) {
    console.warn('SEO Warning: REACT_APP_SITE_URL is not set in .env file. Using default domain.');
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title ? `${title} | ${defaultTitle}` : defaultTitle}</title>
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      {/* Always emitted, never conditional. index.html deliberately carries no
          robots tag: Helmet cannot replace a static tag it did not create, so a
          default there plus a per-page noindex would leave two conflicting
          directives in the document. Keeping a page out of search results does
          not make it private, though: the content still ships in the bundle and
          the URL still works for anyone who has it. */}
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow"}
      />
      <link rel="canonical" href={canonicalUrl ? `${siteUrl}${canonicalUrl}` : siteUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={ogUrl ? `${siteUrl}${ogUrl}` : siteUrl} />
      <meta property="og:title" content={ogTitle || title || defaultTitle} />
      <meta property="og:description" content={ogDescription || description || defaultDescription} />
      <meta property="og:image" content={ogImage ? `${siteUrl}${ogImage}` : `${siteUrl}${defaultOgImage}`} />
      <meta property="og:locale" content="en_CA" />
      <meta property="og:site_name" content="GeneFlou" />

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={ogUrl ? `${siteUrl}${ogUrl}` : siteUrl} />
      <meta name="twitter:title" content={twitterTitle || ogTitle || title || defaultTitle} />
      <meta name="twitter:description" content={twitterDescription || ogDescription || description || defaultDescription} />
      <meta name="twitter:image" content={twitterImage ? `${siteUrl}${twitterImage}` : `${siteUrl}${defaultTwitterImage}`} />
    </Helmet>
  );
};

export default SEO;
